// Paneles de equipo — the "Monday" piece.
//
// A saved report answers one question with one table and carries its own date
// range. A dashboard puts several of those side by side under ONE filter bar,
// so "producción vs. actividad de este mes" is one screen instead of two runs
// with the range typed twice.
//
// The panel stores only the recipe (metric, chart, columns). Nothing is
// precomputed: opening it re-runs the same /reports/team queries the reports
// tab uses, so a widget can never disagree with the report it was built from.
// The cost of that honesty is one request per widget, which is why the builder
// caps them.

import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Edit2, Trash2, Play, Save, X, Share2, ArrowLeft, LayoutDashboard, RefreshCw, Calendar, Sparkles,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import DateField from './DateField.jsx';
import { ReportChart, fmtBy } from './teamReportChart.jsx';
import {
  PanelChart, FiltroMultiple, PALETA, ETIQUETAS_DE_MEDIDA, ETIQUETAS_DE_DIMENSION, tituloPorDefecto, textoDeRango,
} from './PanelChart.jsx';
import {
  rangoDePreset, datosDeLaGrafica, opcionesDeFiltros, etiquetaDe, agenteDe, formatoDe, plantillaProfesional,
  MEDIDAS, DIMENSIONES, MEDIDAS_QUE_NO_SUMAN,
} from '../panelDePolizas.js';
import { cuentaDeComision } from '../comisionDePoliza.js';
import {
  runTeamReport, listTeamDashboards, createTeamDashboard, updateTeamDashboard, deleteTeamDashboard,
  listCustomReports, getClientReportSummary, getSalesSummary, getMetricsSummary, getMembersReport,
} from '../api.js';
import { toast } from './Toast.jsx';
import { confirmDialog } from './ui.jsx';

// Los widgets que preguntan al servidor (uno por consulta al abrir) siguen con
// su tope de 12. Las gráficas de pólizas no preguntan nada — se calculan con
// las pólizas que la app ya tiene — y cuentan aparte, hasta 24 en total. El
// servidor (validateDashboard) aplica los mismos dos topes.
const MAX_WIDGETS = 12;
const MAX_TOTAL = 24;
const cuentaDelServidor = (ws) => ws.filter((w) => w.metric !== 'policyChart').length;

const newWidgetId = () => `w${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export default function TeamDashboardsTab({ lang, METRICS, canManage, policies = [], clients = [], onClientClick }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // dashboard being edited, or {} for new
  const [viewing, setViewing] = useState(null);  // dashboard being rendered

  const refresh = async () => {
    setLoading(true);
    try { setItems(await listTeamDashboards()); }
    catch (err) { toast.error((es ? 'No se pudo cargar: ' : "Couldn't load: ") + err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  if (viewing) {
    return (
      <DashboardView
        lang={lang}
        METRICS={METRICS}
        dashboard={viewing}
        onBack={() => setViewing(null)}
        policies={policies}
        clients={clients}
        onClientClick={onClientClick}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', marginRight: 'auto', lineHeight: 1.5 }}>
          {t('Varios reportes en una pantalla, con un solo filtro para todos.',
             'Several reports on one screen, under a single filter for all of them.')}
        </div>
        {canManage && (
          <button onClick={() => setEditing({})} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', padding: '.55rem 1rem', borderRadius: 9, cursor: 'pointer', fontWeight: 700, fontSize: '.82rem', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            <Plus size={15} /> {t('Nuevo panel', 'New dashboard')}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--c-text2,#7aa3c0)' }}>{t('Cargando…', 'Loading…')}</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem', background: 'var(--c-surface,#0d1c2e)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 14, color: 'var(--c-text2,#7aa3c0)', fontSize: '.88rem' }}>
          {t('Aún no tienes paneles. Crea uno con "Nuevo panel" y agrégale los reportes que quieras ver juntos.',
             'No dashboards yet. Create one with "New dashboard" and add the reports you want to see together.')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '1rem' }}>
          {items.map((d) => {
            const count = Array.isArray(d.widgets) ? d.widgets.length : 0;
            return (
              <div key={d.id} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LayoutDashboard size={18} color="#00d4ff" />
                  <div style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.95rem', flex: 1, minWidth: 0 }}>{d.name}</div>
                  {d.sharedWithAgency && <Share2 size={13} color="#7aa3c0" />}
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)' }}>
                  {count === 1 ? t('1 reporte', '1 report') : t(`${count} reportes`, `${count} reports`)}
                  {!d.isOwner && d.owner ? ` · ${t('por', 'by')} ${d.owner.displayName}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                  <button onClick={() => setViewing(d)} style={btn('primary')}><Play size={12} /> {t('Abrir', 'Open')}</button>
                  {canManage && d.isOwner && <button onClick={() => setEditing(d)} style={btn('ghost')}><Edit2 size={12} /></button>}
                  {canManage && d.isOwner && (
                    <button
                      onClick={async () => {
                        if (await confirmDialog({ message: t(`¿Eliminar "${d.name}"?`, `Delete "${d.name}"?`), danger: true })) {
                          try { await deleteTeamDashboard(d.id); refresh(); } catch (e) { toast.error(e.message); }
                        }
                      }}
                      style={btn('danger')}
                    ><Trash2 size={12} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <DashboardBuilder
          lang={lang}
          METRICS={METRICS}
          initial={editing}
          policies={policies}
          clients={clients}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function btn(kind) {
  const map = {
    primary: { bg: '#00d4ff22', color: '#00d4ff', border: '#00d4ff66' },
    ghost: { bg: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', border: 'var(--c-border,#1a3550)' },
    danger: { bg: '#7f1d1d22', color: '#fca5a5', border: '#ef444466' },
  }[kind];
  return { background: map.bg, color: map.color, border: `1px solid ${map.border}`, padding: '.4rem .65rem', borderRadius: 8, cursor: 'pointer', fontSize: '.75rem', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, fontFamily: 'inherit' };
}

// ── Rendering a dashboard ────────────────────────────────────────────────
const PRESET_OPTS = [
  { v: 'all', es: 'Todo el tiempo', en: 'All time' },
  { v: 'thisMonth', es: 'Este mes', en: 'This month' },
  { v: 'lastMonth', es: 'Mes pasado', en: 'Last month' },
  { v: 'last30', es: 'Últimos 30 días', en: 'Last 30 days' },
  { v: 'last90', es: 'Últimos 90 días', en: 'Last 90 days' },
  { v: 'thisQuarter', es: 'Este trimestre', en: 'This quarter' },
  { v: 'thisYear', es: 'Este año', en: 'This year' },
  { v: 'lastYear', es: 'Año pasado', en: 'Last year' },
  { v: 'custom', es: 'Personalizado', en: 'Custom' },
];
const REFRESH_OPTS = [0, 1, 5, 15, 30];

// Los widgets que no son una métrica de /reports/team. Hasta 2026-10-12 el panel
// filtraba con `METRICS[w.metric]` y estos cuatro NO están en METRICS: un
// widget de Ventas, Métricas, Miembros o Reporte de clientes se guardaba bien,
// salía en el correo, y en pantalla desaparecía sin aviso.
const OTROS_WIDGETS = new Set(['policyChart', 'salesReport', 'metricsReport', 'membersReport', 'clientReport']);
const esWidgetConocido = (METRICS, w) => !!w && (!!METRICS[w.metric] || OTROS_WIDGETS.has(w.metric));

// Filtros de la barra del panel: aplican a TODAS las gráficas de pólizas.
const FILTROS_DEL_PANEL = [
  { key: 'agent', es: 'Agente', en: 'Agent' },
  { key: 'analyst', es: 'Analista', en: 'Analyst' },
  { key: 'state', es: 'Estado', en: 'State' },
  { key: 'type', es: 'Tipo', en: 'Type' },
  { key: 'carrier', es: 'Compañía', en: 'Carrier' },
  { key: 'status', es: 'Estatus', en: 'Status' },
  // Por etiqueta (Lissette 2026-10-22). Filtran, no agrupan: ver panelDePolizas.js.
  { key: 'clientTag', es: 'Tag de cliente', en: 'Client tag' },
  { key: 'policyTag', es: 'Tag de póliza', en: 'Policy tag' },
];

const hace = (desde, ahora, es) => {
  const s = Math.max(0, Math.round((ahora - desde) / 1000));
  if (s < 45) return es ? 'ahora mismo' : 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return es ? `hace ${m} min` : `${m} min ago`;
  return new Date(desde).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

function DashboardView({ lang, METRICS, dashboard, onBack, policies, clients, onClientClick }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const saved = dashboard.filters || {};

  // The filter bar is live state, not the saved value: the agent can retune the
  // range while looking at the panel without having to edit and re-save it.
  const [preset, setPreset] = useState(saved.datePreset || 'all');
  const [from, setFrom] = useState(saved.from || '');
  const [to, setTo] = useState(saved.to || '');
  const [filtros, setFiltros] = useState({});
  const [nonce, setNonce] = useState(0);
  const [drill, setDrill] = useState(null);

  // Actualización sola. Las gráficas de pólizas ya cambian en el momento en
  // que cambian las pólizas de la app (llegan por el aviso en vivo del
  // servidor); esto vuelve a preguntar los widgets que salen del servidor.
  const [refresh, setRefresh] = useState(REFRESH_OPTS.includes(Number(saved.refreshMinutes)) ? Number(saved.refreshMinutes) : 5);
  const [actualizado, setActualizado] = useState(() => Date.now());
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    if (!refresh) return undefined;
    const id = setInterval(() => { setNonce((n) => n + 1); setActualizado(Date.now()); }, refresh * 60000);
    return () => clearInterval(id);
  }, [refresh]);
  useEffect(() => { setActualizado(Date.now()); }, [policies, clients]);
  useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const widgets = useMemo(
    () => (Array.isArray(dashboard.widgets) ? dashboard.widgets : []).filter((w) => esWidgetConocido(METRICS, w)),
    [dashboard, METRICS],
  );
  const win = useMemo(() => rangoDePreset(preset, from, to), [preset, from, to]);
  const hayPolizas = widgets.some((w) => w.metric === 'policyChart');
  const opciones = useMemo(() => (hayPolizas ? opcionesDeFiltros(policies, clients, lang) : {}), [hayPolizas, policies, clients, lang]);
  const hayFiltros = Object.values(filtros).some((v) => v?.length);

  const inp = { padding: '.4rem .6rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.78rem', outline: 'none', fontFamily: 'inherit', fontWeight: 700 };

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', fontSize: '.82rem', marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={14} /> {t('Volver', 'Back')}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '.6rem', marginBottom: '.8rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.35rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
          <LayoutDashboard size={21} color="#00d4ff" /> {dashboard.name}
        </h2>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)' }}>
          {refresh > 0 && <span className="dkt-panel-vivo" />}
          {t('Actualizado', 'Updated')} {hace(actualizado, ahora, es)}
        </span>
        <select value={refresh} onChange={(e) => setRefresh(Number(e.target.value))} style={{ ...inp, cursor: 'pointer' }} title={t('Cada cuánto se actualiza solo', 'How often it refreshes by itself')}>
          {REFRESH_OPTS.map((m) => (
            <option key={m} value={m}>{m === 0 ? t('Sin actualizar solo', 'No auto-refresh') : t(`Cada ${m} min`, `Every ${m} min`)}</option>
          ))}
        </select>
        <button onClick={() => { setNonce((n) => n + 1); setActualizado(Date.now()); }} style={{ ...inp, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={13} /> {t('Actualizar', 'Refresh')}
        </button>
      </div>

      {/* One filter bar for every widget below — the whole point of a panel. */}
      <div className="dkt-panel-barra">
        <Calendar size={15} color="var(--c-text3,#45688a)" />
        <select value={preset} onChange={(e) => setPreset(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          {PRESET_OPTS.map((o) => <option key={o.v} value={o.v}>{es ? o.es : o.en}</option>)}
        </select>
        {preset === 'custom' && (
          <>
            <DateField value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
            <DateField value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
          </>
        )}
        {hayPolizas && FILTROS_DEL_PANEL.filter((f) => (opciones[f.key] || []).length > 0).map((f) => (
          <FiltroMultiple
            key={f.key}
            etiqueta={es ? f.es : f.en}
            opciones={opciones[f.key]}
            value={filtros[f.key] || []}
            onChange={(v) => setFiltros((p) => ({ ...p, [f.key]: v }))}
            lang={lang}
            compacto={false}
          />
        ))}
        {hayFiltros && (
          <button type="button" onClick={() => setFiltros({})} style={{ background: 'none', border: 'none', color: 'var(--c-cyan,#00d4ff)', fontSize: '.76rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {t('Quitar filtros', 'Clear filters')}
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{textoDeRango(win, lang)}</span>
      </div>

      {widgets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem', background: 'var(--c-surface,#0d1c2e)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 14, color: 'var(--c-text2,#7aa3c0)', fontSize: '.88rem' }}>
          {t('Este panel no tiene reportes todavía. Edítalo para agregarle.', 'This dashboard has no reports yet. Edit it to add some.')}
        </div>
      ) : (
        <div className="dkt-panel-grid">
          {widgets.map((w, i) => (
            <div key={w.id || `${w.metric}-${i}`} className={`dkt-w-${anchoDe(w)}`}>
              {w.metric === 'policyChart' ? (
                <PolicyChartWidget
                  lang={lang} widget={w} indice={i} policies={policies} clients={clients}
                  rangoDelPanel={win} filtrosDelPanel={filtros} onDrill={setDrill}
                />
              ) : (
                <Widget lang={lang} METRICS={METRICS} widget={w} win={win} nonce={nonce} />
              )}
            </div>
          ))}
        </div>
      )}

      {drill && <DrillModal lang={lang} drill={drill} clients={clients} onClose={() => setDrill(null)} onClientClick={onClientClick} />}
    </div>
  );
}

/** El ancho guardado, con los paneles viejos (half/full) tal cual. */
const anchoDe = (w) => (['quarter', 'third', 'half', 'twoThirds', 'full'].includes(w.width) ? w.width : 'half');

// Una gráfica de pólizas: receta + pólizas de la app → números (panelDePolizas)
// → dibujo (PanelChart). No pregunta nada al servidor.
function PolicyChartWidget({ lang, widget, indice, policies, clients, rangoDelPanel, filtrosDelPanel, onDrill }) {
  const es = lang === 'es';
  const datos = useMemo(
    () => datosDeLaGrafica(widget, { polizas: policies, clientes: clients, lang, rangoDelPanel, filtrosDelPanel }),
    [widget, policies, clients, lang, rangoDelPanel, filtrosDelPanel],
  );
  const color = PALETA[indice % PALETA.length];
  const propios = Object.entries(widget.filters || {}).filter(([, v]) => Array.isArray(v) && v.length);
  const conRangoPropio = widget.datePreset && widget.datePreset !== 'panel';
  const titulo = widget.title || tituloPorDefecto(widget, lang);
  return (
    <div className="dkt-panel-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: '.75rem' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-text2,#7aa3c0)', textTransform: widget.chartType === 'kpi' || widget.dimension === 'none' ? 'uppercase' : 'none', letterSpacing: widget.chartType === 'kpi' ? '.05em' : 0 }}>
            <span style={{ color: widget.chartType === 'kpi' || widget.dimension === 'none' ? 'var(--c-text2,#7aa3c0)' : 'var(--c-white,#eef6ff)', fontSize: widget.chartType === 'kpi' || widget.dimension === 'none' ? '.72rem' : '.92rem' }}>{titulo}</span>
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {conRangoPropio && <span className="dkt-panel-chip">{textoDeRango(datos.rango, lang)}</span>}
            {propios.map(([k, v]) => (
              <span key={k} className="dkt-panel-chip">{(FILTROS_DEL_PANEL.find((f) => f.key === k) || {})[es ? 'es' : 'en']}: {v.length === 1 ? etiquetaDe(k, v[0], lang) : v.length}</span>
            ))}
          </div>
        </div>
        {widget.dimension && widget.dimension !== 'none' && widget.chartType !== 'kpi' && (
          <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>{formatoDe(datos.medida, datos.total, { corto: datos.total >= 100000 })}</div>
            <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Total</div>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <PanelChart
          widget={widget} datos={datos} polizas={policies} clientes={clients} lang={lang}
          rangoDelPanel={rangoDelPanel} filtrosDelPanel={filtrosDelPanel} color={color}
          onDrill={(f) => onDrill({ titulo: `${titulo} · ${f.name}`, polizas: f.polizas, medida: datos.medida })}
        />
      </div>
      {datos.sinFecha > 0 && (
        <div style={{ marginTop: '.5rem', fontSize: '.66rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.4 }}>
          {es ? `${datos.sinFecha} pólizas no tienen esa fecha y no entran en el rango.` : `${datos.sinFecha} policies have no such date and fall outside the range.`}
        </div>
      )}
    </div>
  );
}

// Las pólizas detrás de una barra, un pedazo o una fila.
function DrillModal({ lang, drill, clients, onClose, onClientClick }) {
  const es = lang === 'es';
  const porId = useMemo(() => new Map((clients || []).map((c) => [c.id, c])), [clients]);
  const filas = [...(drill.polizas || [])].sort((a, b) => String(b.saleDate || '').localeCompare(String(a.saleDate || '')));
  const fecha = (ymd) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '');
    return m ? `${m[2]}/${m[3]}/${m[1]}` : '—';
  };
  const td = { padding: '.45rem .55rem', borderBottom: '1px solid var(--c-border,#1a3550)', whiteSpace: 'nowrap' };
  const th = { ...td, fontSize: '.64rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-text3,#45688a)', textAlign: 'left', position: 'sticky', top: 0, background: 'var(--c-surface,#0d1c2e)' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 16, width: '100%', maxWidth: 980, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '1rem 1.2rem', borderBottom: '1px solid var(--c-border,#1a3550)' }}>
          <div style={{ fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontSize: '1rem', marginRight: 'auto' }}>{drill.titulo}</div>
          <span style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)' }}>{filas.length} {es ? 'pólizas' : 'policies'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ overflow: 'auto', padding: '0 .4rem .6rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)' }}>
            <thead>
              <tr>
                <th style={th}>{es ? 'Cliente' : 'Client'}</th>
                <th style={th}>{es ? 'Tipo' : 'Type'}</th>
                <th style={th}>{es ? 'Compañía' : 'Carrier'}</th>
                <th style={th}>{es ? 'Agente' : 'Agent'}</th>
                <th style={th}>{es ? 'Analista' : 'Analyst'}</th>
                <th style={th}>{es ? 'Venta' : 'Sold'}</th>
                <th style={th}>{es ? 'Estatus' : 'Status'}</th>
                <th style={{ ...th, textAlign: 'right' }}>{es ? 'Prima' : 'Premium'}</th>
                <th style={{ ...th, textAlign: 'right' }}>{es ? 'Comisión' : 'Commission'}</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((p) => {
                const c = porId.get(p.clientId);
                const nombre = p.clientName || (c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name : '') || '—';
                return (
                  <tr key={p.id}>
                    <td style={td}>
                      {c && onClientClick ? (
                        <button type="button" onClick={() => onClientClick(c)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--c-cyan,#00d4ff)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}>{nombre}</button>
                      ) : nombre}
                    </td>
                    <td style={td}>{etiquetaDe('type', p.type, lang)}</td>
                    <td style={td}>{p.carrier || '—'}</td>
                    <td style={td}>{agenteDe(p) || '—'}</td>
                    <td style={td}>{p.analystName || '—'}</td>
                    <td style={td}>{fecha(p.saleDate)}</td>
                    <td style={td}>{etiquetaDe('status', p.status, lang)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{formatoDe('premium', p.monthlyPremium)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{formatoDe('commission', cuentaDeComision(p).total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// One widget = one /reports/team run, drawn with the same chart component the
// reports tab uses. Each widget loads independently so a slow metric doesn't
// hold the rest of the panel blank.
const SUMMARY_DIMS = [
  { key: 'status', es: 'Estado', en: 'Status' },
  { key: 'state', es: 'Estado (US)', en: 'State' },
  { key: 'city', es: 'Ciudad', en: 'City' },
  { key: 'source', es: 'Fuente', en: 'Source' },
  { key: 'agent', es: 'Agente', en: 'Agent' },
  { key: 'language', es: 'Idioma', en: 'Language' },
];

// A client-report widget shows COUNTS ONLY — never the client list. The report
// itself is full of PHI; this summary is what makes the panel (and the emailed
// version of it) safe to share.
function ClientReportWidget({ lang, widget, nonce }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getClientReportSummary(widget.reportId, widget.groupBy || 'status')
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) { setError(e.message || String(e)); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [widget.reportId, widget.groupBy, nonce]);

  const rows = (data?.groups || []).map((g) => ({ name: g.name, count: g.count }));
  const meta = {
    color: '#a855f7',
    columns: [{ key: 'count', label: t('Clientes', 'Clients'), raw: (r) => Number(r.count || 0), unit: 'num', additive: true }],
  };
  const inp = { padding: '.35rem .55rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.75rem', outline: 'none', fontFamily: 'inherit' };

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: '.7rem' }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
          {widget.title || data?.reportName || t('Reporte de clientes', 'Client report')}
        </div>
        {data && (
          <div style={{ fontSize: '.72rem', color: 'var(--c-cyan,#00d4ff)', fontWeight: 700, marginLeft: 'auto' }}>
            {Number(data.total || 0).toLocaleString('en-US')} {t('clientes', 'clients')}
          </div>
        )}
      </div>
      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Cargando…', 'Loading…')}</div>
      ) : error ? (
        <div style={{ background: '#3a1a1a', border: '1px solid #ef4444', borderRadius: 8, padding: '.6rem .8rem', color: '#fca5a5', fontSize: '.78rem' }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Sin clientes que cumplan el filtro.', 'No clients match the filter.')}</div>
      ) : (
        <ReportChart
          rows={rows} meta={meta} chartType={widget.chartType || 'bar'} chartKey="count"
          onChartType={null} onChartKey={null} loading={false} t={t} inp={inp}
        />
      )}
    </div>
  );
}

const SALES_DIMS = [
  { key: 'analyst', es: 'Analista (quién vendió)', en: 'Analyst (who sold)' },
  { key: 'type', es: 'Tipo de póliza', en: 'Policy type' },
  { key: 'carrier', es: 'Compañía', en: 'Carrier' },
  { key: 'plan', es: 'Plan', en: 'Plan' },
  { key: 'processedBy', es: 'Quién procesó', en: 'Processed by' },
  { key: 'agent', es: 'Agente', en: 'Agent' },
  { key: 'month', es: 'Mes (efectiva)', en: 'Month (effective)' },
];
const SALES_MEAS = [
  { key: 'count', es: 'Pólizas', en: 'Policies' },
  { key: 'annualPremium', es: 'Prima anualizada', en: 'Annualized premium' },
];

// The sales report aggregated server-side, so the panel and the emailed copy
// show the same numbers. Commission is absent on purpose — on screen it is
// derived in the browser, and a second derivation here would drift.
function SalesReportWidget({ lang, widget, win, nonce }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const measure = widget.measure || 'count';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getSalesSummary({ dimension: widget.dimension || 'analyst', measure, from: win.from, to: win.to })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) { setError(e.message || String(e)); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [widget.dimension, measure, win.from, win.to, nonce]);

  const rows = data?.groups || [];
  const meta = {
    color: '#00d4ff',
    columns: [
      { key: 'count', label: t('Pólizas', 'Policies'), raw: (r) => Number(r.count || 0), unit: 'num', additive: true },
      { key: 'annualPremium', label: t('Prima anualizada', 'Annualized premium'), raw: (r) => Number(r.annualPremium || 0), unit: 'money', additive: true },
    ],
  };
  const inp = { padding: '.35rem .55rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.75rem', outline: 'none', fontFamily: 'inherit' };
  const fmtTotal = (v) => (measure === 'annualPremium'
    ? '$' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
    : Number(v || 0).toLocaleString('en-US'));

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: '.7rem' }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
          {widget.title || t('Ventas', 'Sales')}
        </div>
        {data && (
          <div style={{ fontSize: '.72rem', color: 'var(--c-cyan,#00d4ff)', fontWeight: 700, marginLeft: 'auto' }}>
            {fmtTotal(data.total)}
          </div>
        )}
      </div>
      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Cargando…', 'Loading…')}</div>
      ) : error ? (
        <div style={{ background: '#3a1a1a', border: '1px solid #ef4444', borderRadius: 8, padding: '.6rem .8rem', color: '#fca5a5', fontSize: '.78rem' }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Sin ventas en este rango.', 'No sales in this range.')}</div>
      ) : (
        <ReportChart
          rows={rows} meta={meta} chartType={widget.chartType || 'bar'} chartKey={measure}
          onChartType={null} onChartKey={null} loading={false} t={t} inp={inp}
          // Agrupado por mes es una línea de tiempo: ordenarla por tamaño la
          // destruiría.
          preserveOrder={(widget.dimension || 'analyst') === 'month'}
        />
      )}
    </div>
  );
}

const MEMBERS_DIMS = [
  { key: 'type', es: 'Tipo de póliza', en: 'Policy type' },
  { key: 'agent', es: 'Agente', en: 'Agent' },
];
const MEMBERS_MEAS = [
  { key: 'uniqueMembers', es: 'Personas distintas', en: 'Distinct people' },
  { key: 'members', es: 'Vidas por póliza', en: 'Lives per policy' },
  { key: 'policies', es: 'Pólizas', en: 'Policies' },
];
const TYPE_LABELS_ES = {
  HEALTH: 'Salud', LIFE: 'Vida', AUTO: 'Auto', HOME: 'Hogar', RENTERS: 'Inquilinos',
  FLOOD: 'Inundación', DENTAL: 'Dental', VISION: 'Visión', DISABILITY: 'Incapacidad',
  LONG_TERM_CARE: 'Cuidado LP', MEDICARE_SUPPLEMENT: 'Medicare Supl.',
  MEDICARE_ADVANTAGE: 'Medicare Adv.', ANNUITY: 'Anualidad', UMBRELLA: 'Paraguas',
  COMMERCIAL: 'Comercial', WORKERS_COMP: 'Comp. Trab.', PET: 'Mascotas', TRAVEL: 'Viaje',
};

// Miembros activos: VIDAS aseguradas, no clientes. Una familia de cuatro en una
// sola póliza es un cliente y cuatro miembros.
//
// Se cuenta siempre sobre la cartera ACTIVA, sin el rango de fechas del panel:
// "miembros activos" es una foto de hoy, no un acumulado del mes. Filtrarla por
// fecha daría un número más chico que el de la pantalla sin explicación.
function MembersReportWidget({ lang, widget, nonce }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const dimension = widget.dimension || 'type';
  const measure = widget.measure || 'uniqueMembers';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getMembersReport({ status: 'ACTIVE' })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) { setError(e.message || String(e)); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [nonce]);

  const rows = (dimension === 'agent' ? (data?.byAgent || []) : (data?.byType || [])).map((r) => ({
    name: dimension === 'agent' ? r.name : (TYPE_LABELS_ES[r.type] || r.type),
    uniqueMembers: r.uniqueMembers,
    members: r.members,
    policies: r.policies,
  }));
  const meta = {
    color: '#f472b6',
    columns: [
      { key: 'uniqueMembers', label: t('Personas distintas', 'Distinct people'), raw: (r) => Number(r.uniqueMembers || 0), unit: 'num', additive: false },
      { key: 'members', label: t('Vidas por póliza', 'Lives per policy'), raw: (r) => Number(r.members || 0), unit: 'num', additive: true },
      { key: 'policies', label: t('Pólizas', 'Policies'), raw: (r) => Number(r.policies || 0), unit: 'num', additive: true },
    ],
  };
  const inp = { padding: '.35rem .55rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.75rem', outline: 'none', fontFamily: 'inherit' };
  const undef = Number(data?.totals?.undefinedPolicies || 0);

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: '.7rem' }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
          {widget.title || t('Miembros activos', 'Active members')}
        </div>
        {data && (
          <div style={{ fontSize: '.72rem', color: 'var(--c-cyan,#00d4ff)', fontWeight: 700, marginLeft: 'auto' }}>
            {Number(data.totals?.uniqueMembers || 0).toLocaleString('en-US')} {t('personas', 'people')}
          </div>
        )}
      </div>
      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Cargando…', 'Loading…')}</div>
      ) : error ? (
        <div style={{ background: '#3a1a1a', border: '1px solid #ef4444', borderRadius: 8, padding: '.6rem .8rem', color: '#fca5a5', fontSize: '.78rem' }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Sin pólizas activas.', 'No active policies.')}</div>
      ) : (
        <ReportChart
          rows={rows} meta={meta} chartType={widget.chartType || 'bar'} chartKey={measure}
          onChartType={null} onChartKey={null} loading={false} t={t} inp={inp}
        />
      )}
      {/* El hueco se dice, no se esconde: una póliza sin marcar a quién cubre no
          aporta vidas, y sin esta línea el total parecería completo. */}
      {undef > 0 && (
        <div style={{ marginTop: '.6rem', fontSize: '.7rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.45 }}>
          {t(`${undef} pólizas activas no tienen marcado a quién cubren, así que no suman vidas.`,
             `${undef} active policies have no covered-people selection, so they add no lives.`)}
        </div>
      )}
    </div>
  );
}

const METRICS_BLOCKS = [
  { key: 'kpis', es: 'Indicadores clave', en: 'Key indicators' },
  { key: 'premium', es: 'Cartera y prima', en: 'Book & premium' },
  { key: 'policyMix', es: 'Mezcla de pólizas', en: 'Policy mix' },
  { key: 'carriers', es: 'Compañías principales', en: 'Top carriers' },
  { key: 'leadSources', es: 'Fuentes de leads', en: 'Lead sources' },
  { key: 'growth', es: 'Crecimiento (12 meses)', en: 'Growth (12 months)' },
  { key: 'financial', es: 'Comisiones y gastos (12 meses)', en: 'Commissions & expenses (12 mo.)' },
];

// A block of the Métricas screen, computed by the server.
//
// Métricas itself adds everything up in the browser out of what the SPA already
// loaded — fine for a screen, useless for an 8am email. The server answers the
// same questions (utils/agencyAnalytics.ts, shared with Analítica de agencia),
// so the panel, the screen and the emailed copy cannot disagree.
//
// The block arrives already shaped as { columns, rows }, so the widget does no
// arithmetic of its own — it only draws what came back. `note` is the part that
// is NOT covered (an empty retention cohort, expenses that belong to the whole
// agency); it is printed rather than left for the reader to assume.
function MetricsReportWidget({ lang, widget, win, nonce }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const block = widget.block || 'kpis';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getMetricsSummary({ block, from: win.from, to: win.to })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) { setError(e.message || String(e)); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [block, win.from, win.to, nonce]);

  const cols = data?.columns || [];
  const meta = {
    color: '#34d399',
    columns: cols.map((c) => ({
      key: c.key,
      label: c.label,
      raw: (r) => (r[c.key] === null || r[c.key] === undefined ? null : Number(r[c.key])),
      unit: c.money ? 'money' : 'num',
      // Los indicadores mezclan conteos con porcentajes y promedios: sumarlos
      // daría un total sin significado, así que no se suman.
      additive: block !== 'kpis' && block !== 'premium',
    })),
  };
  const inp = { padding: '.35rem .55rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.75rem', outline: 'none', fontFamily: 'inherit' };
  // Un valor ausente se escribe con raya, nunca con cero: "0% de retención" y
  // "todavía no hay a quién medir" son frases distintas.
  const fmtCell = (v, c) => {
    if (v === null || v === undefined || v === '') return '—';
    return c.money
      ? '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })
      : Number(v).toLocaleString('en-US');
  };

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: '.7rem' }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
          {widget.title || data?.title || t('Métricas', 'Metrics')}
        </div>
      </div>
      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Cargando…', 'Loading…')}</div>
      ) : error ? (
        <div style={{ background: '#3a1a1a', border: '1px solid #ef4444', borderRadius: 8, padding: '.6rem .8rem', color: '#fca5a5', fontSize: '.78rem' }}>{error}</div>
      ) : (data?.rows || []).length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Sin datos en este rango.', 'No data in this range.')}</div>
      ) : data.chartKey ? (
        <ReportChart
          rows={data.rows} meta={meta} chartType={widget.chartType || 'bar'} chartKey={data.chartKey}
          onChartType={null} onChartKey={null} loading={false} t={t} inp={inp}
          // Crecimiento y finanzas son meses: se leen en orden, no por tamaño.
          preserveOrder={block === 'growth' || block === 'financial'}
        />
      ) : (
        // Los bloques sin gráfico lo son a propósito: mezclan un porcentaje con
        // un conteo y con un promedio, y una barra los pondría a competir como
        // si midieran lo mismo. Se dibujan como tabla, igual que en el correo.
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={String(r.name)} style={{ background: i % 2 ? 'var(--c-surface2,#112538)' : 'transparent' }}>
                <td style={{ padding: '.35rem .5rem', color: 'var(--c-text2,#7aa3c0)' }}>{r.name}</td>
                {cols.map((c) => (
                  <td key={c.key} style={{ padding: '.35rem .5rem', textAlign: 'right', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap' }}>
                    {fmtCell(r[c.key], c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data?.note && (
        <div style={{ marginTop: '.6rem', fontSize: '.7rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.45 }}>
          {data.note}
        </div>
      )}
    </div>
  );
}

function Widget({ lang, METRICS, widget, win, nonce }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  if (widget.metric === 'metricsReport') {
    return <MetricsReportWidget lang={lang} widget={widget} win={win} nonce={nonce} />;
  }
  if (widget.metric === 'membersReport') {
    return <MembersReportWidget lang={lang} widget={widget} nonce={nonce} />;
  }
  if (widget.metric === 'clientReport') {
    return <ClientReportWidget lang={lang} widget={widget} nonce={nonce} />;
  }
  if (widget.metric === 'salesReport') {
    return <SalesReportWidget lang={lang} widget={widget} win={win} nonce={nonce} />;
  }
  const meta = METRICS[widget.metric];
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    runTeamReport(widget.metric, { from: win.from, to: win.to })
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) { setError(e.message || String(e)); setRows([]); } })
      .finally(() => { if (alive) setLoading(false); });
    // Cancelled on unmount / filter change so a slow response can't overwrite
    // the results of a newer one.
    return () => { alive = false; };
  }, [widget.metric, win.from, win.to, nonce]);

  const chartable = (meta?.columns || []).filter((c) => c.raw);
  const chartKey = chartable.some((c) => c.key === widget.chartKey)
    ? widget.chartKey
    : (chartable.find((c) => c.additive)?.key || chartable[0]?.key || '');

  const inp = { padding: '.35rem .55rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.75rem', outline: 'none', fontFamily: 'inherit' };
  const Icon = meta?.icon;

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: '.7rem' }}>
        {Icon && <Icon size={15} color={meta.color} />}
        <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
          {widget.title || meta?.title}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{t('Cargando…', 'Loading…')}</div>
      ) : error ? (
        <div style={{ background: '#3a1a1a', border: '1px solid #ef4444', borderRadius: 8, padding: '.6rem .8rem', color: '#fca5a5', fontSize: '.78rem' }}>{error}</div>
      ) : !rows || rows.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>
          {widget.metric === 'goals'
            ? t('Sin metas activas en el período actual.', 'No active goals in the current period.')
            : t('Sin datos para este rango.', 'No data for this range.')}
        </div>
      ) : widget.chartType === 'none' ? (
        // "Solo tabla". ReportChart no dibuja tabla —en Reportes de Equipo la
        // tabla va aparte, debajo— así que aquí el widget salía como una caja
        // vacía (hasta 2026-10-12). La tabla se arma aquí con las mismas columnas.
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.76rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '.35rem .5rem', color: 'var(--c-text3,#45688a)', fontSize: '.64rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>{t('Nombre', 'Name')}</th>
                {chartable.map((c) => (
                  <th key={c.key} style={{ textAlign: 'right', padding: '.35rem .5rem', color: 'var(--c-text3,#45688a)', fontSize: '.64rem', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={String(r.name ?? i)} style={{ background: i % 2 ? 'var(--c-surface2,#112538)' : 'transparent' }}>
                  <td style={{ padding: '.35rem .5rem', color: 'var(--c-text,#c8dff0)', fontWeight: 600 }}>{r.name}</td>
                  {chartable.map((c) => (
                    <td key={c.key} style={{ padding: '.35rem .5rem', textAlign: 'right', fontWeight: 700, color: 'var(--c-white,#eef6ff)', whiteSpace: 'nowrap' }}>{fmtBy(c.unit, c.raw(r))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ReportChart
          rows={rows}
          meta={meta}
          chartType={widget.chartType || 'bar'}
          chartKey={chartKey}
          /* Read-only inside a panel: the chart choice belongs to the saved
             widget, so changing it here would look saved and wouldn't be. */
          onChartType={null}
          onChartKey={null}
          loading={false}
          t={t}
          inp={inp}
        />
      )}
    </div>
  );
}

// ── Builder ──────────────────────────────────────────────────────────────
function DashboardBuilder({ lang, METRICS, initial, onClose, onSaved, policies, clients }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const f0 = initial.filters || {};

  const [name, setName] = useState(initial.name || '');
  const [widgets, setWidgets] = useState(
    Array.isArray(initial.widgets) && initial.widgets.length
      ? initial.widgets.map((w) => ({ ...w, id: w.id || newWidgetId() }))
      : [],
  );
  const [datePreset, setDatePreset] = useState(f0.datePreset || 'all');
  const [from, setFrom] = useState(f0.from || '');
  const [to, setTo] = useState(f0.to || '');
  const [shared, setShared] = useState(!!initial.sharedWithAgency);
  // Envío programado. '' = apagado, que es como nacen todos los paneles.
  const [freq, setFreq] = useState(initial.scheduleFrequency || '');
  const [hour, setHour] = useState(Number.isInteger(initial.scheduleHour) ? initial.scheduleHour : 8);
  const [emails, setEmails] = useState(
    Array.isArray(initial.scheduleRecipients) ? initial.scheduleRecipients.join(', ') : '',
  );
  const [refreshMinutes, setRefreshMinutes] = useState(REFRESH_OPTS.includes(Number(f0.refreshMinutes)) ? Number(f0.refreshMinutes) : 5);
  const [busy, setBusy] = useState(false);
  const opciones = useMemo(() => opcionesDeFiltros(policies, clients, lang), [policies, clients, lang]);
  // Saved client reports available as widgets. Loaded once; empty list just
  // means the option isn't offered.
  const [clientReports, setClientReports] = useState([]);
  useEffect(() => {
    listCustomReports().then((r) => setClientReports(r || [])).catch(() => {});
  }, []);

  const nuevaGrafica = (over = {}) => ({
    id: newWidgetId(), metric: 'policyChart', measure: 'policies', dimension: 'agent', chartType: 'bar',
    width: 'half', dateField: 'saleDate', datePreset: 'panel', filters: {}, ...over,
  });
  // "Agregar" pone una gráfica de pólizas: es la que más se arma y no gasta
  // consultas. Desde el selector se cambia a cualquier otro reporte.
  const addWidget = () => {
    if (widgets.length >= MAX_TOTAL) return;
    setWidgets((p) => [...p, nuevaGrafica()]);
  };
  const usarPlantilla = () => {
    setWidgets((p) => [...p, ...plantillaProfesional(newWidgetId)].slice(0, MAX_TOTAL));
    if (!name.trim()) setName(t('Panel de ventas', 'Sales dashboard'));
  };
  const mover = (id, paso) => setWidgets((p) => {
    const i = p.findIndex((w) => w.id === id);
    const j = i + paso;
    if (i < 0 || j < 0 || j >= p.length) return p;
    const n = p.slice();
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const patch = (id, over) => setWidgets((p) => p.map((w) => {
    if (w.id !== id) return w;
    const next = { ...w, ...over };
    // Gráfica de pólizas: cada forma pide su agrupación. Un indicador es UN
    // número (sin agrupar); una línea es una línea de TIEMPO (por mes); y una
    // dona de clientes mentiría (un cliente con dos agentes cuenta en los dos).
    if (next.metric === 'policyChart') {
      if (over.metric && w.metric !== 'policyChart') return { ...nuevaGrafica(), id: w.id, width: w.width || 'half' };
      if (!MEDIDAS.includes(next.measure)) next.measure = 'policies';
      if (!DIMENSIONES.includes(next.dimension)) next.dimension = 'agent';
      if (over.chartType === 'kpi') next.dimension = 'none';
      else if (over.chartType === 'line') next.dimension = 'month';
      else if (over.chartType && next.dimension === 'none') next.dimension = 'agent';
      if (over.dimension === 'none') next.chartType = 'kpi';
      else if (over.dimension && next.chartType === 'kpi') next.chartType = over.dimension === 'month' ? 'line' : 'bar';
      else if (over.dimension && over.dimension !== 'month' && next.chartType === 'line') next.chartType = 'bar';
      if (next.chartType === 'donut' && MEDIDAS_QUE_NO_SUMAN.has(next.measure)) next.chartType = 'bar';
      return next;
    }
    // Saliendo de una gráfica de pólizas: sus formas (indicador, línea, dona,
    // tabla) no existen en los demás reportes.
    if (w.metric === 'policyChart' && over.metric) {
      next.chartType = 'bar';
      delete next.measure; delete next.dimension; delete next.dateField; delete next.datePreset; delete next.filters;
    }
    // `clientReport` is not in METRICS — it points at a saved client report and
    // always charts a single count, so none of the column juggling applies.
    // Reading METRICS[next.metric].columns for it would throw.
    if (next.metric === 'clientReport') {
      if (!next.groupBy) next.groupBy = 'status';
      next.chartType = next.chartType === 'pie' ? 'pie' : 'bar';
      return next;
    }
    // Tampoco es una entrada de METRICS: el bloque llega ya armado del
    // servidor, con sus columnas y su gráfico. Algunos bloques mezclan unidades
    // (un porcentaje junto a un conteo) y por eso vienen sin gráfico — el
    // widget lo respeta en vez de forzar una barra.
    if (next.metric === 'metricsReport') {
      if (!next.block) next.block = 'kpis';
      next.chartType = next.chartType === 'pie' ? 'pie' : 'bar';
      return next;
    }
    // Miembros: "personas distintas" NO se puede repartir en una torta —
    // la misma persona en salud y en dental cuenta una vez en cada tipo, así
    // que los pedazos suman más que el total. ReportChart ya lo bloquea por
    // `additive: false`; esto evita guardar una receta que no se puede dibujar.
    if (next.metric === 'membersReport') {
      if (!next.dimension) next.dimension = 'type';
      if (!next.measure) next.measure = 'uniqueMembers';
      if (next.chartType === 'pie' && next.measure === 'uniqueMembers') next.chartType = 'bar';
      return next;
    }
    // Same reason: not a METRICS entry, and both its measures are sums, so a
    // pie is always honest here.
    if (next.metric === 'salesReport') {
      if (!next.dimension) next.dimension = 'analyst';
      if (!next.measure) next.measure = 'count';
      next.chartType = next.chartType === 'pie' ? 'pie' : 'bar';
      return next;
    }
    // Switching metric invalidates the charted column, and a pie over a
    // non-additive column would claim its slices split a whole.
    if (over.metric && over.metric !== w.metric) {
      const cols = (METRICS[over.metric]?.columns || []).filter((c) => c.raw);
      next.chartKey = cols.find((c) => c.additive)?.key || cols[0]?.key || '';
    }
    const cols = (METRICS[next.metric]?.columns || []).filter((c) => c.raw);
    const picked = cols.find((c) => c.key === next.chartKey);
    if (next.chartType === 'pie' && picked && !picked.additive) next.chartType = 'bar';
    return next;
  }));

  const save = async () => {
    if (!name.trim()) { toast.error(t('Ponle un nombre', 'Add a name')); return; }
    setBusy(true);
    // Se parte por comas y saltos de línea: la gente pega listas de las dos
    // formas, y rechazar una de ellas se siente como un error de la app.
    const missingReport = widgets.find((w) => w.metric === 'clientReport' && !w.reportId);
    if (missingReport) {
      toast.error(t('Elige el reporte de clientes en cada widget que lo use', 'Pick the client report on every widget that uses one'));
      setBusy(false);
      return;
    }
    const recipients = emails.split(/[,;\n]/).map((e) => e.trim()).filter(Boolean);
    const bad = recipients.find((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (freq && bad) { toast.error(t(`Correo inválido: ${bad}`, `Invalid address: ${bad}`)); setBusy(false); return; }
    if (freq && recipients.length === 0) {
      toast.error(t('Agrega al menos un correo para el envío programado', 'Add at least one address for the scheduled send'));
      setBusy(false);
      return;
    }
    if (cuentaDelServidor(widgets) > MAX_WIDGETS) {
      toast.error(t(`Máximo ${MAX_WIDGETS} reportes que no sean gráficas de pólizas`, `At most ${MAX_WIDGETS} reports that aren't policy charts`));
      setBusy(false);
      return;
    }
    const payload = {
      name: name.trim(),
      widgets,
      filters: {
        datePreset,
        from: datePreset === 'custom' ? from : '',
        to: datePreset === 'custom' ? to : '',
        refreshMinutes,
      },
      sharedWithAgency: shared,
      scheduleFrequency: freq || null,
      scheduleHour: Number(hour),
      scheduleRecipients: recipients,
    };
    try {
      if (initial.id) await updateTeamDashboard(initial.id, payload);
      else await createTeamDashboard(payload);
      toast.success(t('Panel guardado', 'Dashboard saved'));
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const inp = { width: '100%', padding: '.5rem .65rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lbl = { fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5, display: 'block' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 16, padding: '1.5rem', width: '100%', maxWidth: 780, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--c-white,#eef6ff)' }}>
            {initial.id ? t('Editar panel', 'Edit dashboard') : t('Nuevo panel', 'New dashboard')}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={lbl}>{t('Nombre', 'Name')}</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('ej. Panel del lunes', 'e.g. Monday panel')} style={inp} />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={lbl}>{t('Filtro del panel (aplica a todos)', 'Panel filter (applies to all)')}</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} style={{ ...inp, flex: 1, minWidth: 160 }}>
              {PRESET_OPTS.map((o) => <option key={o.v} value={o.v}>{es ? o.es : o.en}</option>)}
            </select>
            <select value={refreshMinutes} onChange={(e) => setRefreshMinutes(Number(e.target.value))} style={{ ...inp, width: 'auto' }} title={t('Cada cuánto se actualiza solo', 'How often it refreshes by itself')}>
              {REFRESH_OPTS.map((m) => (
                <option key={m} value={m}>{m === 0 ? t('Sin actualizar solo', 'No auto-refresh') : t(`Actualizar cada ${m} min`, `Refresh every ${m} min`)}</option>
              ))}
            </select>
          </div>
          {datePreset === 'custom' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <DateField value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
              <DateField value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
            </div>
          )}
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 5, lineHeight: 1.45 }}>
            {t('Es el valor con el que abre el panel; al verlo se puede cambiar sin volver a guardarlo. «Cumplimiento de metas» siempre mide el período que corre ahora.',
               'This is what the panel opens with; you can change it while viewing without re-saving. "Goal attainment" always measures the period running now.')}
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ ...lbl, marginBottom: 0, marginRight: 'auto' }}>
              {t('Gráficas y reportes del panel', 'Panel charts and reports')} ({widgets.length}/{MAX_TOTAL})
            </label>
            <button
              type="button"
              onClick={usarPlantilla}
              disabled={widgets.length >= MAX_TOTAL}
              title={t('Agrega indicadores, una línea por mes y gráficas por agente, analista, compañía, tipo y estado', 'Adds KPIs, a monthly line and charts by agent, analyst, carrier, type and state')}
              style={{ ...btn('ghost'), marginRight: 6, opacity: widgets.length >= MAX_TOTAL ? .45 : 1 }}
            >
              <Sparkles size={12} /> {t('Plantilla profesional', 'Pro template')}
            </button>
            <button
              type="button"
              onClick={addWidget}
              disabled={widgets.length >= MAX_TOTAL}
              style={{ ...btn('primary'), opacity: widgets.length >= MAX_TOTAL ? .45 : 1, cursor: widgets.length >= MAX_TOTAL ? 'not-allowed' : 'pointer' }}
            >
              <Plus size={12} /> {t('Agregar', 'Add')}
            </button>
          </div>

          {widgets.some((w) => w.metric === 'policyChart') && (
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.45, marginBottom: 8 }}>
              {t('Las gráficas de pólizas se calculan con las pólizas de la app, con la misma comisión de Comisiones por Póliza, y cambian solas cuando alguien guarda una venta. Los filtros de cada gráfica son solo suyos; los de la barra del panel aplican a todas. En el panel, un clic en una barra o un pedazo abre sus pólizas. No viajan en el correo programado.',
                 'Policy charts are computed from the app’s policies, with the same commission as Commissions by Policy, and change by themselves when someone saves a sale. Each chart’s filters are its own; the panel bar’s apply to all. On the panel, clicking a bar or slice opens its policies. They are not included in the scheduled email.')}
            </div>
          )}
          {widgets.length === 0 && (
            <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', padding: '.6rem 0' }}>
              {t('Agrega al menos un reporte para que el panel muestre algo.', 'Add at least one report so the panel shows something.')}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            {widgets.map((w, i) => {
              const sinCupo = cuentaDelServidor(widgets) >= MAX_WIDGETS;
              const cols = (METRICS[w.metric]?.columns || []).filter((c) => c.raw);
              const picked = cols.find((c) => c.key === w.chartKey);
              const pieOk = picked?.additive === true;
              return (
                <div key={w.id} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>#{i + 1}</span>
                    <select value={w.metric} onChange={(e) => patch(w.id, { metric: e.target.value })} style={{ ...inp, flex: 1, fontSize: '.78rem', padding: '.35rem .5rem' }}>
                      <option value="policyChart">{t('★ Gráfica de pólizas (interactiva)', '★ Policy chart (interactive)')}</option>
                      <optgroup label={t('Reportes del servidor', 'Server reports')} disabled={w.metric === 'policyChart' && sinCupo}>
                        {Object.entries(METRICS).map(([id, m]) => <option key={id} value={id}>{m.title}</option>)}
                        <option value="salesReport">{t('Reporte de ventas', 'Sales report')}</option>
                        <option value="metricsReport">{t('Métricas…', 'Metrics…')}</option>
                        <option value="membersReport">{t('Miembros activos', 'Active members')}</option>
                        {clientReports.length > 0 && (
                          <option value="clientReport">{t('Reporte de clientes…', 'Client report…')}</option>
                        )}
                      </optgroup>
                    </select>
                    <button type="button" onClick={() => mover(w.id, -1)} disabled={i === 0} style={{ ...btn('ghost'), opacity: i === 0 ? .4 : 1 }} title={t('Subir', 'Move up')}><ChevronUp size={12} /></button>
                    <button type="button" onClick={() => mover(w.id, 1)} disabled={i === widgets.length - 1} style={{ ...btn('ghost'), opacity: i === widgets.length - 1 ? .4 : 1 }} title={t('Bajar', 'Move down')}><ChevronDown size={12} /></button>
                    <button type="button" onClick={() => setWidgets((p) => p.filter((x) => x.id !== w.id))} style={btn('danger')}><Trash2 size={12} /></button>
                  </div>
                  {w.metric === 'policyChart' ? (
                    <PolicyChartEditor w={w} patch={patch} lang={lang} inp={inp} opciones={opciones} />
                  ) : w.metric === 'membersReport' ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <select value={w.dimension || 'type'} onChange={(e) => patch(w.id, { dimension: e.target.value })} style={{ ...inp, flex: 1, minWidth: 130, fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        {MEMBERS_DIMS.map((d) => <option key={d.key} value={d.key}>{es ? d.es : d.en}</option>)}
                      </select>
                      <select value={w.measure || 'uniqueMembers'} onChange={(e) => patch(w.id, { measure: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        {MEMBERS_MEAS.map((m) => <option key={m.key} value={m.key}>{es ? m.es : m.en}</option>)}
                      </select>
                      <select value={w.chartType || 'bar'} onChange={(e) => patch(w.id, { chartType: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        <option value="bar">{t('Barras', 'Bars')}</option>
                        <option value="pie" disabled={(w.measure || 'uniqueMembers') === 'uniqueMembers'}>{t('Torta', 'Pie')}</option>
                      </select>
                      <AnchoSelect value={w.width} onChange={(v) => patch(w.id, { width: v })} lang={lang} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }} />
                      <div style={{ flexBasis: '100%', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.4 }}>
                        {t('Vidas aseguradas, no clientes: una familia de cuatro en una póliza es un cliente y cuatro miembros. Cuenta siempre la cartera activa de hoy, sin el rango de fechas del panel. "Personas distintas" no va en torta: la misma persona en salud y en dental cuenta una vez en cada tipo, así que los pedazos sumarían más que el total.',
                           'Insured lives, not clients: a family of four on one policy is one client and four members. Always counts today\'s active book, ignoring the panel date range. "Distinct people" cannot be a pie: the same person counts once under health and once under dental, so the slices would add up to more than the whole.')}
                      </div>
                    </div>
                  ) : w.metric === 'metricsReport' ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <select value={w.block || 'kpis'} onChange={(e) => patch(w.id, { block: e.target.value })} style={{ ...inp, flex: 1, minWidth: 150, fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        {METRICS_BLOCKS.map((b) => <option key={b.key} value={b.key}>{es ? b.es : b.en}</option>)}
                      </select>
                      <select value={w.chartType || 'bar'} onChange={(e) => patch(w.id, { chartType: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        <option value="bar">{t('Barras', 'Bars')}</option>
                        <option value="pie">{t('Torta', 'Pie')}</option>
                      </select>
                      <AnchoSelect value={w.width} onChange={(v) => patch(w.id, { width: v })} lang={lang} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }} />
                      <div style={{ flexBasis: '100%', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.4 }}>
                        {t('Los mismos números de la pantalla de Métricas, pero calculados en el servidor para que puedan viajar en el correo. Indicadores clave y Cartera se muestran como tabla: mezclan porcentajes con conteos y una barra los compararía como si fueran lo mismo.',
                           'The same numbers as the Metrics screen, computed on the server so they can travel by email. Key indicators and Book render as a table: they mix percentages with counts, and bars would compare them as if they were the same thing.')}
                      </div>
                    </div>
                  ) : w.metric === 'salesReport' ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <select value={w.dimension || 'analyst'} onChange={(e) => patch(w.id, { dimension: e.target.value })} style={{ ...inp, flex: 1, minWidth: 130, fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        {SALES_DIMS.map((d) => <option key={d.key} value={d.key}>{es ? d.es : d.en}</option>)}
                      </select>
                      <select value={w.measure || 'count'} onChange={(e) => patch(w.id, { measure: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        {SALES_MEAS.map((m) => <option key={m.key} value={m.key}>{es ? m.es : m.en}</option>)}
                      </select>
                      <select value={w.chartType || 'bar'} onChange={(e) => patch(w.id, { chartType: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        <option value="bar">{t('Barras', 'Bars')}</option>
                        <option value="pie">{t('Torta', 'Pie')}</option>
                      </select>
                      <AnchoSelect value={w.width} onChange={(v) => patch(w.id, { width: v })} lang={lang} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }} />
                      <div style={{ flexBasis: '100%', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.4 }}>
                        {t('Cuenta pólizas o suma prima anualizada. La comisión no está aquí: en pantalla se calcula en el navegador, y repetir ese cálculo aquí haría que los dos números se separaran con el tiempo.',
                           'Counts policies or sums annualized premium. Commission is absent: on screen it is derived in the browser, and repeating that here would let the two numbers drift apart.')}
                      </div>
                    </div>
                  ) : w.metric === 'clientReport' ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <select
                        value={w.reportId || ''}
                        onChange={(e) => patch(w.id, { reportId: e.target.value })}
                        style={{ ...inp, flex: 1, minWidth: 140, fontSize: '.75rem', padding: '.3rem .5rem' }}
                      >
                        <option value="">{t('Elige un reporte…', 'Pick a report…')}</option>
                        {clientReports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                      <select value={w.groupBy || 'status'} onChange={(e) => patch(w.id, { groupBy: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }}>
                        {SUMMARY_DIMS.map((d) => <option key={d.key} value={d.key}>{es ? d.es : d.en}</option>)}
                      </select>
                      <AnchoSelect value={w.width} onChange={(v) => patch(w.id, { width: v })} lang={lang} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }} />
                      <div style={{ flexBasis: '100%', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.4 }}>
                        {t('Muestra solo cuántos clientes hay en cada grupo — nunca la lista con nombres. Por eso se puede enviar por correo.',
                           'Shows only how many clients fall in each group — never the list with names. That is what makes it safe to email.')}
                      </div>
                    </div>
                  ) : (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <select value={w.chartType || 'bar'} onChange={(e) => patch(w.id, { chartType: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }}>
                      <option value="bar">{t('Barras', 'Bars')}</option>
                      <option value="pie" disabled={!pieOk}>{t('Torta', 'Pie')}</option>
                      <option value="none">{t('Solo tabla', 'Table only')}</option>
                    </select>
                    <select value={w.chartKey || ''} onChange={(e) => patch(w.id, { chartKey: e.target.value })} style={{ ...inp, flex: 1, minWidth: 120, fontSize: '.75rem', padding: '.3rem .5rem' }}>
                      {cols.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    <AnchoSelect value={w.width} onChange={(v) => patch(w.id, { width: v })} lang={lang} style={{ ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' }} />
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: '1rem', borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '1rem' }}>
          <label style={lbl}>{t('Enviar por correo', 'Email delivery')}</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={freq} onChange={(e) => setFreq(e.target.value)} style={{ ...inp, flex: 1, minWidth: 140 }}>
              <option value="">{t('No enviar', 'Do not send')}</option>
              <option value="daily">{t('Todos los días', 'Every day')}</option>
              <option value="weekly">{t('Cada lunes', 'Every Monday')}</option>
              <option value="monthly">{t('El 1 de cada mes', 'The 1st of each month')}</option>
            </select>
            <select value={hour} onChange={(e) => setHour(Number(e.target.value))} disabled={!freq} style={{ ...inp, width: 'auto', opacity: freq ? 1 : .5 }}>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          {freq && (
            <>
              <input
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder={t('correo@ejemplo.com, otro@ejemplo.com', 'name@example.com, other@example.com')}
                style={{ ...inp, marginTop: 8 }}
              />
              <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 5, lineHeight: 1.45 }}>
                {t('Separa los correos con comas. Pueden ser personas que no usen Deskte. La hora es la de tu agencia (se configura en Mi Agencia). El correo lleva las gráficas de barras y un PDF adjunto con el reporte completo.',
                   'Separate addresses with commas. They can be people who do not use Deskte. The time is your agency’s (set in My Agency). The email carries bar charts plus a PDF attachment with the full report.')}
              </div>
            </>
          )}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: '1.25rem' }}>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} style={{ width: 17, height: 17, cursor: 'pointer' }} />
          <span style={{ fontSize: '.85rem', color: 'var(--c-text,#c8dff0)' }}>{t('Compartir con mi agencia (solo lectura)', 'Share with my agency (read-only)')}</span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
          <button onClick={onClose} style={{ padding: '.6rem 1.1rem', borderRadius: 9, background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', border: '1px solid var(--c-border,#1a3550)', cursor: 'pointer', fontWeight: 700, fontSize: '.82rem' }}>{t('Cancelar', 'Cancel')}</button>
          <button onClick={save} disabled={busy} style={{ padding: '.6rem 1.25rem', borderRadius: 9, background: 'linear-gradient(135deg,#00d4ff,#00a8c8)', color: '#0a1628', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '.82rem', opacity: busy ? .6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Save size={14} /> {busy ? t('Guardando…', 'Saving…') : t('Guardar', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

const ANCHOS = [
  { v: 'quarter', es: 'Pequeño (¼)', en: 'Small (¼)' },
  { v: 'third', es: 'Un tercio', en: 'One third' },
  { v: 'half', es: 'Mitad', en: 'Half' },
  { v: 'twoThirds', es: 'Dos tercios', en: 'Two thirds' },
  { v: 'full', es: 'Ancho completo', en: 'Full width' },
];
function AnchoSelect({ value, onChange, lang, style }) {
  return (
    <select value={anchoDe({ width: value })} onChange={(e) => onChange(e.target.value)} style={style} title={lang === 'es' ? 'Tamaño' : 'Size'}>
      {ANCHOS.map((a) => <option key={a.v} value={a.v}>{lang === 'es' ? a.es : a.en}</option>)}
    </select>
  );
}

const TIPOS_DE_GRAFICA = [
  { v: 'kpi', es: 'Indicador (un número)', en: 'KPI (one number)' },
  { v: 'bar', es: 'Barras', en: 'Bars' },
  { v: 'line', es: 'Línea por mes', en: 'Monthly line' },
  { v: 'donut', es: 'Dona', en: 'Donut' },
  { v: 'table', es: 'Tabla', en: 'Table' },
];
const CAMPOS_FECHA = [
  { v: 'saleDate', es: 'Fecha de venta', en: 'Sale date' },
  { v: 'effectiveDate', es: 'Fecha efectiva', en: 'Effective date' },
  { v: 'renewalDate', es: 'Fecha de renovación', en: 'Renewal date' },
];

// La receta de una gráfica de pólizas: qué medir, cómo agruparla, qué forma,
// qué fechas y qué filtros PROPIOS (además de los de la barra del panel).
function PolicyChartEditor({ w, patch, lang, inp, opciones }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const sel = { ...inp, width: 'auto', fontSize: '.75rem', padding: '.3rem .5rem' };
  const lbl = { fontSize: '.64rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 3 };
  const campo = (titulo, hijo, flex = '1 1 140px') => (
    <div style={{ flex, minWidth: 0 }}>
      <span style={lbl}>{titulo}</span>
      {hijo}
    </div>
  );
  const filtros = w.filters || {};
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        value={w.title || ''}
        onChange={(e) => patch(w.id, { title: e.target.value.slice(0, 120) })}
        placeholder={tituloPorDefecto(w, lang)}
        style={{ ...inp, fontSize: '.8rem', padding: '.4rem .55rem' }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {campo(t('Medir', 'Measure'), (
          <select value={w.measure || 'policies'} onChange={(e) => patch(w.id, { measure: e.target.value })} style={{ ...sel, width: '100%' }}>
            {MEDIDAS.map((m) => <option key={m} value={m}>{es ? ETIQUETAS_DE_MEDIDA[m].es : ETIQUETAS_DE_MEDIDA[m].en}</option>)}
          </select>
        ))}
        {campo(t('Agrupar por', 'Group by'), (
          <select value={w.dimension || 'agent'} onChange={(e) => patch(w.id, { dimension: e.target.value })} style={{ ...sel, width: '100%' }}>
            {DIMENSIONES.map((d) => <option key={d} value={d}>{es ? ETIQUETAS_DE_DIMENSION[d].es : ETIQUETAS_DE_DIMENSION[d].en}</option>)}
          </select>
        ))}
        {campo(t('Gráfica', 'Chart'), (
          <select value={w.chartType || 'bar'} onChange={(e) => patch(w.id, { chartType: e.target.value })} style={{ ...sel, width: '100%' }}>
            {TIPOS_DE_GRAFICA.map((g) => (
              <option key={g.v} value={g.v} disabled={g.v === 'donut' && MEDIDAS_QUE_NO_SUMAN.has(w.measure)}>{es ? g.es : g.en}</option>
            ))}
          </select>
        ))}
        {campo(t('Tamaño', 'Size'), (
          <AnchoSelect value={w.width} onChange={(v) => patch(w.id, { width: v })} lang={lang} style={{ ...sel, width: '100%' }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {campo(t('Fecha que cuenta', 'Date that counts'), (
          <select value={w.dateField || 'saleDate'} onChange={(e) => patch(w.id, { dateField: e.target.value })} style={{ ...sel, width: '100%' }}>
            {CAMPOS_FECHA.map((c) => <option key={c.v} value={c.v}>{es ? c.es : c.en}</option>)}
          </select>
        ))}
        {campo(t('Rango de fechas', 'Date range'), (
          <select value={w.datePreset || 'panel'} onChange={(e) => patch(w.id, { datePreset: e.target.value })} style={{ ...sel, width: '100%' }}>
            <option value="panel">{t('El del panel', "The panel's")}</option>
            {PRESET_OPTS.map((o) => <option key={o.v} value={o.v}>{es ? o.es : o.en}</option>)}
          </select>
        ))}
        {w.datePreset === 'custom' && (
          <>
            {campo(t('Desde', 'From'), <DateField value={w.from || ''} onChange={(e) => patch(w.id, { from: e.target.value })} style={{ ...sel, width: '100%' }} />)}
            {campo(t('Hasta', 'To'), <DateField value={w.to || ''} onChange={(e) => patch(w.id, { to: e.target.value })} style={{ ...sel, width: '100%' }} />)}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {FILTROS_DEL_PANEL.filter((f) => (opciones[f.key] || []).length > 0).map((f) => (
          <FiltroMultiple
            key={f.key}
            etiqueta={es ? f.es : f.en}
            opciones={opciones[f.key]}
            value={filtros[f.key] || []}
            onChange={(v) => patch(w.id, { filters: { ...filtros, [f.key]: v } })}
            lang={lang}
            compacto
          />
        ))}
      </div>
    </div>
  );
}
